import React, { useState, useEffect, useContext, useReducer } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { AppContext } from "../AppContext";
import MultiImageUpload from "../components/MultiImage/MultiImageUpload";

export const EditPost = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [originalPost, setOriginalPost] = useState(null);
    const { port, token, addAlert } = useContext(AppContext);
    const initialState = {
        name: "",
        address: "",
        rent: "",
        description: "",
        images: [],
        contact: 0,
        city: "",
        startDate: "",
        endDate: "",
        subleaseRooms: 1,
        totalBedrooms: 1,
        totalBathrooms: 1,
        preferences: "None"
    };

    const postReducer = (state, action) => {
        switch (action.type) {
            case 'SET_FULL_POST':
                return action.payload;
            case 'UPDATE_FIELD':
                return {
                    ...state,
                    [action.field]: action.value
                };
            case 'UPDATE_IMAGES':
                return {
                    ...state,
                    images: action.images
                };
            default:
                return state;
        }
    };

    const [post, dispatch] = useReducer(postReducer, initialState);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const fetchPost = async () => {
            axios.get(`${port}/posts/${id}`)
                .then((response) => {
                    const postData = response.data;
                    if (postData.images && postData.images.length > 0) {
                        postData.images = postData.images.map(image => {
                            // Validate image data
                            if (!image.data?.data || !image.contentType) {
                                console.error('Invalid image data:', image);
                                return null;
                            }

                            // Convert the existing image to match new image upload format
                            const blob = new Blob([new Uint8Array(image.data.data)], { type: image.contentType });
                            const file = new File([blob], 'existingImage.jpg', { type: image.contentType });
                            return {
                                preview: URL.createObjectURL(blob),
                                file: file,
                                isNew: true
                            };
                        }).filter(Boolean); // Remove invalid images
                    }
                    setOriginalPost(postData);
                    dispatch({ type: 'SET_FULL_POST', payload: postData });
                })
                .catch((error) => {
                    addAlert("Error fetching post", "danger", 5000);
                });
        };
        fetchPost();
    }, [addAlert, id, port]);
    
    // Modify the handleSubmit function:
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const formData = new FormData();
    
        // Add non-image fields
        Object.keys(post).forEach(key => {
            if (key !== 'images') {
                formData.append(key, post[key]);
            }
        });
        
        // Only append new images if they were changed
        if (post.images && post.images.length > 0) {
            post.images.forEach(image => {
                if (image.file) {
                    formData.append('images', image.file);
                }
            });
        }
    
        try {
            await axios.put(`${port}/posts/${id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                }
            });
            
            addAlert("Post updated successfully", "success");
            // Wait for state updates to complete before navigating
            await new Promise(resolve => setTimeout(resolve, 0));
            navigate('/my-posts', { replace: true }); // Use replace to prevent navigation stack issues
        } catch (error) {
            addAlert("Error updating post", "danger");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        dispatch({ type: 'UPDATE_FIELD', field: name, value: value });
    };

    const handleFileChange = (updatedImages) => {
        dispatch({ type: 'UPDATE_IMAGES', images: updatedImages });   
    };

    const handleCancel = () => {
        const confirmCancel = window.confirm("Are you sure you want to cancel? Any unsaved changes will be lost.");
        if (confirmCancel) {
            navigate("/my-posts");
        }
    };

    if (!originalPost) {
        return <div className="container text-center mt-5">Loading...</div>;
    }

    return (
        <div className="container">
            <div className="edit-post">
                <h1 className="text-center mb-4">Edit Post</h1>
                <form onSubmit={handleSubmit} encType="multipart/form-data">
                    <div className="mb-3">
                        <label htmlFor="name" className="form-label">Name</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            id="name" 
                            name="name" 
                            value={post.name}
                            onChange={handleChange} 
                            required 
                        />
                    </div>

                    <div className="mb-3">
                        <label htmlFor="address" className="form-label">Street Address</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            id="address" 
                            name="address" 
                            value={post.address}
                            onChange={handleChange} 
                            required 
                        />
                    </div>

                    <div className="mb-3">
                        <label htmlFor="city" className="form-label">City</label>
                        <select 
                            className="form-select" 
                            id="city" 
                            name="city" 
                            value={post.city}
                            onChange={handleChange} 
                            required
                        >
                            <option value="">Select a city</option>
                            <option value="Champaign">Champaign</option>
                            <option value="Urbana">Urbana</option>
                            <option value="Savoy">Savoy</option>
                        </select>
                    </div>

                    <div className="mb-3">
                        <label htmlFor="rent" className="form-label">Rent</label>
                        <input 
                            type="number" 
                            className="form-control" 
                            id="rent" 
                            name="rent" 
                            value={post.rent}
                            onChange={handleChange} 
                            required 
                        />
                    </div>

                    <div className="mb-3">
                        <label htmlFor="subleaseRooms" className="form-label">Sublease Rooms</label>
                        <input 
                            type="number" 
                            className="form-control" 
                            id="subleaseRooms" 
                            name="subleaseRooms" 
                            min="1" 
                            value={post.subleaseRooms}
                            onChange={handleChange} 
                            required 
                            placeholder="How many rooms are you subleasing?" 
                        />
                    </div>

                    <div className="mb-3">
                        <label htmlFor="totalBedrooms" className="form-label">Bedrooms</label>
                        <input 
                            type="number" 
                            className="form-control" 
                            id="totalBedrooms" 
                            name="totalBedrooms" 
                            min="1" 
                            value={post.totalBedrooms}
                            onChange={handleChange} 
                            required 
                            placeholder="Total number of bedrooms in the apartment" 
                        />
                    </div>

                    <div className="mb-3">
                        <label htmlFor="totalBathrooms" className="form-label">Bathrooms</label>
                        <input 
                            type="number" 
                            className="form-control" 
                            id="totalBathrooms" 
                            name="totalBathrooms" 
                            min="1" 
                            value={post.totalBathrooms}
                            onChange={handleChange} 
                            required 
                            placeholder="Total number of bathrooms in the apartment" 
                        />
                    </div>

                    <div className="mb-3">
                        <label htmlFor="contact" className="form-label">Contact</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            id="contact" 
                            name="contact" 
                            value={post.contact}
                            onChange={handleChange} 
                            required 
                            placeholder="Phone Number, Email, Instagram, Snapchat etc." 
                        />
                    </div>

                    <div className="mb-3">
                        <label htmlFor="startDate" className="form-label">Sublease Start Date</label>
                        <input 
                            type="date" 
                            className="form-control" 
                            id="startDate" 
                            name="startDate" 
                            value={post.startDate ? post.startDate.split('T')[0] : ''}
                            onChange={handleChange} 
                            required 
                        />
                    </div>

                    <div className="mb-3">
                        <label htmlFor="endDate" className="form-label">Sublease End Date</label>
                        <input 
                            type="date" 
                            className="form-control" 
                            id="endDate" 
                            name="endDate" 
                            value={post.endDate ? post.endDate.split('T')[0] : ''}
                            onChange={handleChange} 
                            required 
                        />
                    </div>

                    <div className="mb-3">
                        <label htmlFor="preferences" className="form-label">Preferences</label>
                        <select 
                            className="form-select" 
                            id="preferences" 
                            name="preferences" 
                            value={post.preferences}
                            onChange={handleChange} 
                            required
                        >
                            <option value="None">None</option>
                            <option value="Only Males">Only Males</option>
                            <option value="Only Females">Only Females</option>
                        </select>
                    </div>

                    <div className="mb-3">
                        <label htmlFor="images" className="form-label">Images (Optional)</label>
                        <MultiImageUpload 
                            images={post.images || []}
                            onChange={handleFileChange}
                            required={false}
                        />
                        <small className="text-muted d-block mt-1">
                            Leave empty to keep existing images
                        </small>
                    </div>

                    <div className="mb-3">
                        <label htmlFor="description" className="form-label">Other Information</label>
                        <textarea 
                            className="form-control" 
                            id="description" 
                            name="description" 
                            value={post.description}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="d-grid gap-2">
                        <button 
                            type="submit" 
                            className="btn btn-primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Saving Changes...' : 'Save Changes'}
                        </button>
                        <button 
                            type="button" 
                            className="btn btn-secondary"
                            onClick={handleCancel}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
